import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HandleModalProps } from "@/types/props";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { emptyLanguageCertification } from "@/models/empty";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { ZodError } from "../ZodDisplayError";
import { languageCertificateSchema, tLanguageCertificateSchema } from "../validations/languageCertificate";
import { ethnicCodeAndNameList, languageLevelOptions, nationalityCodeAndNameList, requestTypeList } from "../validations/shared";
import { NgocInput } from "../ui/ngocInput";
import { getLocalDate } from "@/lib/utils/func";

export default function LanguageCertificateModal({ isOpen, onClose, onAdd, onUpdate, editData, mode }: HandleModalProps) {
	const {
		control,
		register,
		handleSubmit,
		trigger,
		reset,
		setValue,
		formState: { errors, isValid },
	} = useForm<tLanguageCertificateSchema>({
		resolver: zodResolver(languageCertificateSchema),
		defaultValues: emptyLanguageCertification,
		mode: "all",
	});

	const submit = (data: tLanguageCertificateSchema) => {
		switch (mode) {
			case "create":
				onAdd(data);
				break;
			case "edit":
				onUpdate(data);
				break;
		}
		onClose();
	};

	useEffect(() => {
		switch (mode) {
			case "create":
				reset(emptyLanguageCertification);
				break;
			case "edit":
				reset({
					...editData,
					currentCertificateNumber: editData.certificateNumber,
					placeOfBirth: editData.placeOfBirth || "",
					ethnicCode: editData.ethnicCode || "",
					nationalityCode: editData.nationalityCode || "",
					notes: editData.notes || "",
					dateOfBirth: editData.dateOfBirth ? getLocalDate(editData.dateOfBirth) : emptyLanguageCertification.dateOfBirth,
					signDate: editData.signDate ? getLocalDate(editData.signDate) : emptyLanguageCertification.signDate,
					// examDate: editData.examDate ? getLocalDate(editData.examDate) : emptyLanguageCertification.examDate,
					// issueDate: editData.issueDate ? getLocalDate(editData.issueDate) : emptyLanguageCertification.issueDate,
					// expiryDate: editData.expiryDate ? getLocalDate(editData.expiryDate) : emptyLanguageCertification.expiryDate,
					courseStart: editData.courseStart ? getLocalDate(editData.courseStart) : emptyLanguageCertification.courseStart,
					courseEnd: editData.courseEnd ? getLocalDate(editData.courseEnd) : emptyLanguageCertification.courseEnd,
				});
				trigger();
				break;
		}
	}, [isOpen, editData, mode, reset, trigger]);

	const [openDateOfBirthSelected, setOpenDateOfBirthSelected] = useState(false);
	const [openCourseStartSelected, setOpenCourseStartSelected] = useState(false);
	const [openCourseEndSelected, setOpenCourseEndSelected] = useState(false);
	// const [openIssueSelected, setOpenIssueSelected] = useState(false);
	const [openSignDateSelected, setOpenSignDateSelected] = useState(false);
	const onError = (e: any) => {
		console.log(e);
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className='max-w-6xl'>
				<DialogHeader>
					<DialogTitle>{mode === "create" ? "Thêm mới" : "Cập nhật Chứng Chỉ Ngoại Ngữ"} </DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(submit, onError)} id='languageCertificateForm' className='grid grid-cols-2 gap-2 px-1 py-4 max-h-[75dvh] overflow-y-auto'>
					{/* Thông tin cá nhân */}
					<div className='col-span-2'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin cá nhân</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Họ và tên </Label>
						<NgocInput placeholder='Nhập họ tên' maxLength={250} {...register("studentName")} />
						<ZodError message={errors.studentName?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số báo danh </Label>
						<NgocInput placeholder='Nhập Số báo danh' maxLength={250} {...register("studentId")} />
						<ZodError message={errors.studentId?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số CCCD </Label>
						<NgocInput placeholder='Nhập số CCCD' maxLength={12} {...register("cccd")} />
						<ZodError message={errors.cccd?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày sinh </Label>
						<Controller
							control={control}
							name='dateOfBirth'
							render={({ field }) => (
								<Popover open={openDateOfBirthSelected} onOpenChange={(value) => { setOpenDateOfBirthSelected(value); if (!value) trigger("dateOfBirth"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày sinh" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenDateOfBirthSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.dateOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Nơi sinh </Label>
						<NgocInput placeholder='Nhập nơi sinh' maxLength={250} {...register("placeOfBirth")} />
						<ZodError message={errors.placeOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Giới tính </Label>
						<Controller
							control={control}
							name='gender'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn giới tính' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Nam'>Nam</SelectItem>
										<SelectItem value='Nữ'>Nữ</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.gender?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<Controller
							control={control}
							name='ethnicCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetEthnic = ethnicCodeAndNameList.find((x) => x.code == value);
										setValue("ethnicity", targetEthnic!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn dân tộc' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{ethnicCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<Controller
							control={control}
							name='nationalityCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetNation = nationalityCodeAndNameList.find((x) => x.code == value);
										setValue("nationality", targetNation!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn quốc tịch' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{nationalityCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.nationalityCode?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label>Mã Dân tộc</Label>
						<NgocInput placeholder='Nhập mã dân tộc' {...register("ethnicCode")} />
						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<NgocInput placeholder='Nhập dân tộc' {...register("ethnicity")} />
						<ZodError message={errors.ethnicity?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Mã quốc tịch</Label>
						<NgocInput placeholder='Nhập mã quốc tịch' {...register("nationalityCode")} autoComplete='off' />

						<ZodError message={errors.nationalityCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<NgocInput placeholder='Nhập quốc tịch' {...register("nationality")} autoComplete='off' />

						<ZodError message={errors.nationality?.message} />
					</div> */}

					{/* Thông tin chứng chỉ */}
					<div className='col-span-2 mt-4'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin chứng chỉ</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngoại ngữ</Label>
						<Controller
							control={control}
							name='language'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn ngoại ngữ' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Tiếng Anh'>Tiếng Anh</SelectItem>
										<SelectItem value='Tiếng Nhật'>Tiếng Nhật</SelectItem>
										<SelectItem value='Tiếng Trung'>Tiếng Trung</SelectItem>
										<SelectItem value='Tiếng Hàn'>Tiếng Hàn</SelectItem>
										<SelectItem value='Tiếng Pháp'>Tiếng Pháp</SelectItem>
										<SelectItem value='Tiếng Đức'>Tiếng Đức</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.language?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Trình độ</Label>
						<Controller
							control={control}
							name='level'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn trình độ' />
									</SelectTrigger>
									<SelectContent>
										{languageLevelOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.level?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Quyết định công nhận số</Label>
						<NgocInput placeholder='Nhập quyết định' maxLength={250} {...register("recognitionDecisionNumber")} autoComplete='off' />

						<ZodError message={errors.recognitionDecisionNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Quyết định thành lập hội đồng</Label>
						<NgocInput placeholder='Nhập quyết định' maxLength={250} {...register("decisionToEstablishCouncilNumber")} autoComplete='off' />

						<ZodError message={errors.decisionToEstablishCouncilNumber?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className='required'>Xếp loại </Label>
						<Controller
							control={control}
							name='classification'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn xếp loại' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Xuất sắc'>Xuất sắc</SelectItem>
										<SelectItem value='Giỏi'>Giỏi</SelectItem>
										<SelectItem value='Khá'>Khá</SelectItem>
										<SelectItem value='Trung bình'>Trung bình</SelectItem>
										<SelectItem value='Đạt'>Đạt</SelectItem>
										<SelectItem value='Không đạt'>Không đạt</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.classification?.message} />
					</div> */}
					{/* <div className='space-y-1'>
						<Label>Xếp loại ID</Label>
						<Controller
							control={control}
							name='classificationId'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn ID xếp loại' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='01'>01 - Xuất sắc</SelectItem>
										<SelectItem value='02'>02 - Giỏi</SelectItem>
										<SelectItem value='03'>03 - Khá</SelectItem>
										<SelectItem value='04'>04 - Trung bình</SelectItem>
										<SelectItem value='05'>05 - Đạt</SelectItem>
										<SelectItem value='06'>06 - Không đạt</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.classificationId?.message} />
					</div> */}

					<div className='space-y-1'>
						<Label className='required'>Số hiệu bằng </Label>
						<NgocInput placeholder='Nhập số hiệu' maxLength={250} {...register("certificateNumber")} />
						<ZodError message={errors.certificateNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số vào sổ cấp bằng </Label>
						<NgocInput placeholder='Nhập số vào sổ' maxLength={50} {...register("registrationNumber")} />
						<ZodError message={errors.registrationNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Khoá học bắt đầu </Label>
						<Controller
							control={control}
							name='courseStart'
							render={({ field }) => (
								<Popover open={openCourseStartSelected} onOpenChange={setOpenCourseStartSelected}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày khóa học bắt đầu" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenCourseStartSelected(false);
													trigger("courseStart");
													trigger("courseEnd");
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.courseStart?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Khoá học kết thúc </Label>
						<Controller
							control={control}
							name='courseEnd'
							render={({ field }) => (
								<Popover open={openCourseEndSelected} onOpenChange={setOpenCourseEndSelected}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày khóa học kết thúc" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												field.onChange(getLocalDate(value));
												setOpenCourseEndSelected(false);
												trigger("courseEnd");
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.courseEnd?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className='required'>Ngày cấp </Label>
						<Controller
							control={control}
							name='issueDate'
							render={({ field }) => (
								<Popover open={openIssueSelected} onOpenChange={setOpenIssueSelected}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={new Date(field.value)}
											onSelect={(value: any) => {
												const localDate = new Date(value);
												localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
												field.onChange(localDate);
												setOpenIssueSelected(false);
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.courseEnd?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label className='required'>Hội đồng thi</Label>
						<NgocInput placeholder='Nhập tên hội đồng thi' maxLength={250} {...register("examCenter")} />
						<ZodError message={errors.examCenter?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Khoá học</Label>
						<NgocInput placeholder='Nhập tên khóa học' maxLength={250} {...register("course")} autoComplete='off' />

						<ZodError message={errors.course?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Người ký </Label>
						<NgocInput placeholder='Nhập tên người ký' maxLength={250} {...register("signedBy")} />
						<ZodError message={errors.signedBy?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Chức danh người ký</Label>
						<NgocInput placeholder='Nhập chức danh người ký' maxLength={250} {...register("signerTitle")} />
						<ZodError message={errors.signerTitle?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày ký </Label>
						<Controller
							control={control}
							name='signDate'
							render={({ field }) => (
								<Popover open={openSignDateSelected} onOpenChange={(value) => { setOpenSignDateSelected(value); if (!value) trigger("signDate"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày ký" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenSignDateSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.signDate?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestTypeId'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetRequestType = requestTypeList.find((x) => x.code == value);
										setValue("requestType", targetRequestType!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										{requestTypeList.map((rq) => {
											return (
												<SelectItem key={rq.code} value={rq.code}>
													({rq.code}) {rq.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.requestTypeId?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestType'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
									disabled
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Cấp mới'>Cấp mới</SelectItem>
										<SelectItem value='Chỉnh sửa'>Chỉnh sửa</SelectItem>
										<SelectItem value='Huỷ bằng'>Huỷ bằng</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.requestType?.message} />
					</div> */}
					{/* 
					<div className='space-y-1'>
						<Label>Điểm nghe</Label>
						<NgocInput {...register("listeningScore")} type='number' placeholder='Nhập điểm nghe' />
					</div>
					<div className='space-y-1'>
						<Label>Điểm nói</Label>
						<NgocInput {...register("speakingScore")} type='number' placeholder='Nhập điểm nói' />
					</div>
					<div className='space-y-1'>
						<Label>Điểm đọc</Label>
						<NgocInput {...register("readingScore")} type='number' placeholder='Nhập điểm đọc' />
					</div>
					<div className='space-y-1'>
						<Label>Điểm viết</Label>
						<NgocInput {...register("writingScore")} type='number' placeholder='Nhập Điểm viết' />
					</div>
					<div className='space-y-1'>
						<Label>Tổng điểm</Label>
						<NgocInput {...register("totalScore")} type='number' placeholder='Nhập tổng điểm' />
					</div> */}

					<div className='col-span-2 space-y-1'>
						<Label>Ghi chú</Label>
						<NgocInput rows={3} maxLength={500} placeholder='Nhập ghi chú (nếu có)' {...register("notes")} />
						<ZodError message={errors.notes?.message} />
					</div>
				</form>
				<div className='flex justify-end gap-2'>
					<Button variant='destructive' onClick={onClose}>
						Hủy
					</Button>
					<Button disabled={!isValid} variant={"add"} type='submit' form='languageCertificateForm'>
						Lưu
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
